import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import { useDispatch } from 'react-redux';

const ITEMS_PER_PAGE = 50;

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  if (current > 3) pages.push('...');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
};

export const SchedulesHomepage = () => {

  const dispatch = useDispatch();
  const logoutUser = () => {
    dispatch({ type: 'LOG_OUT', payload: null });
    sessionStorage.setItem('currentUser', null);
    window.location = "/";
  };

  const [schedules, setSchedules] = useState([]);
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const filterSchedules = (data, term) => {
    if (!term) return data;
    return data.filter(item => {
      const searchStringLocationCode = item && item[21] ? item[21].value : "";
      const searchStringAddress = item && item[36] ? item[36].value : "";
      const searchStringType = item && item[20] ? item[20].value : "";
      return searchStringLocationCode.toLowerCase().includes(term) ||
        searchStringAddress.toLowerCase().includes(term) ||
        searchStringType.toLowerCase().includes(term);
    });
  };

  useEffect(() => {
    let isMounted = true;

    const fetchInitial = async () => {
      setIsLoading(true);
      const response = await fetch("/apischedules?limit=50", { credentials: 'include' });
      const data = await response.json();
      if (!isMounted) return;
      if (data && data.length > 0) {
        setSchedules(data);
        setFilteredSchedules(data);
      }
      setIsLoading(false);

      // Phase 2: fetch all records in background
      const allResponse = await fetch("/apischedules", { credentials: 'include' });
      const allData = await allResponse.json();
      if (!isMounted) return;
      if (allData && allData.length > 0) {
        setSchedules(allData);
        setFilteredSchedules(searchTerm ? filterSchedules(allData, searchTerm) : allData);
      }
      setIsFullyLoaded(true);
    };

    fetchInitial();
    return () => { isMounted = false; };
  }, []);

  const redirectToschedulePage = (e, scheduleId) => {
    navigate(`/schedule/${scheduleId}`, {state: {id: scheduleId}});
  };

  const handleChangeInput = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    setFilteredSchedules(filterSchedules(schedules, term));
    setCurrentPage(1);
  };

  // Pagination
  const totalPages = Math.ceil(filteredSchedules.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedSchedules = filteredSchedules.slice(startIndex, endIndex);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredSchedules.length / ITEMS_PER_PAGE));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [filteredSchedules, currentPage]);

  const headers = (
    <tr>
      <th>White Castle Location Number</th>
      <th>Location Code</th>
      <th>Address</th>
      <th>Pick-Up Type</th>
      <th>Container Count</th>
      <th>Container Size</th>
      <th>Pick-Up Freq.</th>
      <th>Pick-Up Days</th>
      <th>Has Dumpster Lock</th>
    </tr>
  );

  return (
    <div className="schedules">
    <div className="scheduleDetailsWrapper">
    <h2>All Waste & Recycling Schedules</h2>
    <div className="searchInputWrapper inputWrapper">
      <input placeholder="Search records..." onChange={handleChangeInput}></input>
    </div>
    {isLoading ? (
      <div className="skeleton-wrapper">
        <p style={{color: '#666', fontSize: '14px', marginBottom: '10px', marginTop: '10px'}}>
          Loading schedules...
        </p>
        <table className="skeleton-table">
          <tbody>
            {headers}
            {Array.from({ length: 15 }).map((_, idx) => (
              <tr key={idx}>
                <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                <td className="tableCell"><div className="skeleton-cell skeleton-cell-long"></div></td>
                <td className="tableCell"><div className="skeleton-cell skeleton-cell-medium"></div></td>
                <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                <td className="tableCell"><div className="skeleton-cell skeleton-cell-medium"></div></td>
                <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <>
        <p style={{color: '#666', fontSize: '14px', marginBottom: '10px', marginTop: '10px'}}>
          Showing {filteredSchedules.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredSchedules.length)} of {filteredSchedules.length} schedules
          {!isFullyLoaded && <span> (loading more...)</span>}
        </p>
        <table id="tableWrapper">
          <tbody>
          {headers}
            {paginatedSchedules.length > 0 && paginatedSchedules.map((schedule, idx) => {
              const scheduleId = schedule && schedule[3].value ? schedule[3].value : null;
              const hasDumpsterLock = schedule && schedule[44] ? schedule[44].value : "";
              const hasDumpsterLockText = hasDumpsterLock === '1' ? "Yes" : "";
              return (
                <tr key={idx} onClick={(e) => redirectToschedulePage(e, scheduleId)} className="tableRow">
                  <td className="tableCell">{schedule && schedule[66] ? schedule[66].value : ""}</td>
                  <td className="tableCell">{schedule && schedule[21] ? schedule[21].value : ""}</td>
                  <td className="tableCell">{schedule && schedule[36] ? schedule[36].value : ""}</td>
                  <td className="tableCell">{schedule && schedule[20] ? schedule[20].value : ""}</td>
                  <td className="tableCell">{schedule && schedule[33] ? schedule[33].value : ""}</td>
                  <td className="tableCell">{schedule && schedule[15] ? schedule[15].value : ""}</td>
                  <td className="tableCell">{schedule && schedule[16] ? schedule[16].value : ""}</td>
                  <td className="tableCell">{schedule && schedule[61] ? schedule[61].value : ""}</td>
                  <td className="tableCell">{hasDumpsterLockText}</td>
                </tr>
              )
            })}
            </tbody>
        </table>
        {totalPages > 1 && (
          <div className="pagination-wrapper">
            <button
              className={`pagination-btn ${currentPage === 1 ? 'pagination-btn--disabled' : ''}`}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            {getPageNumbers(currentPage, totalPages).map((page, idx) => (
              page === '...' ? (
                <span key={`ellipsis-${idx}`} className="pagination-btn pagination-btn--disabled">...</span>
              ) : (
                <button
                  key={page}
                  className={`pagination-btn ${currentPage === page ? 'pagination-btn--active' : ''}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              )
            ))}
            <button
              className={`pagination-btn ${currentPage === totalPages ? 'pagination-btn--disabled' : ''}`}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </>
    )}
    </div>
    </div>
  )
}
